/**
 * KBDocumentList — document table with filters, pagination, and actions.
 * US-007 Step 1: shows all KB documents with status, version badges, delete.
 * WCAG 2.1 AA: table with scope, live region for count, labelled filters.
 */
import { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { useKBDocuments, type KBDocumentResponse, type DocType } from '@/hooks/useKBDocuments';
import { KBDocumentRow } from './KBDocumentRow';
import { DOC_TYPE_LABELS } from './kbDocConstants';

const JURISDICTIONS = ['EU', 'PT', 'DE', 'FR', 'ES', 'NL', 'PL'];
const PAGE_SIZE = 10;

interface KBDocumentListProps {
  onDelete: (doc: KBDocumentResponse) => void;
  onUpload: () => void;
}

export function KBDocumentList({ onDelete, onUpload }: KBDocumentListProps) {
  const [docTypeFilter, setDocTypeFilter] = useState<DocType | ''>('');
  const [jurisdictionFilter, setJurisdictionFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: docs, isLoading, error } = useKBDocuments({
    doc_type: docTypeFilter || undefined,
    jurisdiction: jurisdictionFilter || undefined,
  });

  const totalDocs = docs?.length ?? 0;
  const globalCount = docs?.filter((d) => d.client_id === null).length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / PAGE_SIZE));
  const paged = docs?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [];

  const toggleExpand = (id: string) => { setExpandedId((prev) => (prev === id ? null : id)); };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-label="Loading documents">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive py-4 text-center">
        Failed to load knowledge base documents. Please refresh the page.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label htmlFor="kb-filter-type" className="sr-only">Filter by document type</label>
          <select id="kb-filter-type" value={docTypeFilter}
            onChange={(e) => { setDocTypeFilter(e.target.value as DocType | ''); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All types</option>
            {(Object.keys(DOC_TYPE_LABELS) as DocType[]).map((k) => (
              <option key={k} value={k}>{DOC_TYPE_LABELS[k]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="kb-filter-jurisdiction" className="sr-only">Filter by jurisdiction</label>
          <select id="kb-filter-jurisdiction" value={jurisdictionFilter}
            onChange={(e) => { setJurisdictionFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <option value="">All jurisdictions</option>
            {JURISDICTIONS.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        {totalDocs > 0 && (
          <p className="ml-auto text-xs text-muted-foreground" aria-live="polite">
            {totalDocs} {totalDocs === 1 ? 'document' : 'documents'}
            {globalCount > 0 && ` (${globalCount} global, ${totalDocs - globalCount} client-specific)`}
          </p>
        )}
      </div>

      {/* Empty state */}
      {totalDocs === 0 && (
        <EmptyState icon={BookOpen} title="No documents in knowledge base"
          description="Upload legislation to improve compliance analysis."
          action={<Button onClick={onUpload}>Upload Document</Button>} />
      )}

      {/* Table */}
      {totalDocs > 0 && (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm" aria-label="Knowledge base documents">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-left">
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Title</th>
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Type</th>
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Jurisdiction</th>
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Version</th>
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Indexed</th>
                  <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th scope="col" className="px-4 py-3 sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((doc) => (
                  <KBDocumentRow key={doc.id} doc={doc}
                    expanded={expandedId === doc.id}
                    onToggleExpand={() => { toggleExpand(doc.id); }}
                    onDelete={onDelete} />
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page === 1}
                onClick={() => { setPage((p) => Math.max(1, p - 1)); }}>Previous</Button>
              <span className="text-xs text-muted-foreground" aria-live="polite">
                Page {page} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={page === totalPages}
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}>Next</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
